import React, { useEffect, useMemo, useReducer, useState } from "react";
import {
    fetchProfileSport,
    saveCollegeToMyList,
    searchColleges
} from "../../services/collegeWizardApi";
import { fetchSports, groupSportsByCategory } from "../../utils/athleteApi";
import "../../styles/college-wizard.css";

/** Bumped when step flow or filter defaults change so stale session data does not mis-route users. */
const STORAGE_KEY = "collegeWizardStateV4";
const US_STATES = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
    "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
    "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"
];

const SIZE_OPTION_COPY = {
    SMALL: "Fewer than 3,000 students",
    MEDIUM: "About 3,000 to 15,000 students",
    LARGE: "More than 15,000 students",
    NO_PREFERENCE: "No school size preference"
};

const initialState = {
    step: 1,
    sports: [],
    divisions: [],
    states: [],
    localeTypes: [],
    sizePreference: "NO_PREFERENCE",
    schoolType: ""
};

function getInitialWizardState() {
    if (typeof window === "undefined") {
        return initialState;
    }
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
        return initialState;
    }
    try {
        const parsed = JSON.parse(raw);
        const sports = Array.isArray(parsed?.sports)
            ? parsed.sports
            : (typeof parsed?.sport === "string" && parsed.sport.trim() ? [parsed.sport.trim()] : []);
        return { ...initialState, ...parsed, sports };
    } catch (e) {
        return initialState;
    }
}

function reducer(state, action) {
    switch (action.type) {
        case "hydrate":
            return { ...state, ...action.payload };
        case "set":
            return { ...state, [action.key]: action.value };
        case "toggle": {
            const values = new Set(state[action.key]);
            if (values.has(action.value)) values.delete(action.value);
            else values.add(action.value);
            return { ...state, [action.key]: Array.from(values) };
        }
        case "step":
            return { ...state, step: action.value };
        case "reset":
            return { ...initialState, sports: state.sports };
        default:
            return state;
    }
}

function sizeRange(preference) {
    switch (preference) {
        case "SMALL":
            return { maxSize: 2999 };
        case "MEDIUM":
            return { minSize: 3000, maxSize: 15000 };
        case "LARGE":
            return { minSize: 15001 };
        default:
            return {};
    }
}

function dedupeCollegesByIpeds(colleges) {
    const seen = new Set();
    return colleges.filter((college) => {
        const key = college?.ipedsId ?? college?.name;
        if (!key || seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}

function divisionColor(division) {
    if (division === "NCAA_D1") return "cw-badge d1";
    if (division === "NCAA_D2") return "cw-badge d2";
    if (division === "NCAA_D3") return "cw-badge d3";
    if (division === "NAIA") return "cw-badge naia";
    return "cw-badge njcaa";
}

/** Display label for division filter pills (values sent to API remain NCAA_D1, etc.). */
function divisionFilterLabel(division) {
    if (division === "NCAA_D1") return "NCAA D1";
    if (division === "NCAA_D2") return "NCAA D2";
    if (division === "NCAA_D3") return "NCAA D3";
    return division;
}

function collegeSearchErrorMessage(error) {
    const status = error?.status;
    if (status === 400) {
        return "Some of those filters could not be used. Please adjust your search.";
    }
    if (status === 429) {
        return "You've reached the search limit for now. Please try again later.";
    }
    if (status === 503) {
        return "College search is temporarily unavailable. Please try again.";
    }
    return "Failed to load colleges. Please try again.";
}

export default function CollegeWizard({ athleteUsername, onCollegeSaved }) {
    const [state, dispatch] = useReducer(reducer, initialState, getInitialWizardState);
    const [profileSport, setProfileSport] = useState("");
    const [sportCatalog, setSportCatalog] = useState([]);
    const [results, setResults] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState("");
    const [error, setError] = useState("");
    const [showLocationInfo, setShowLocationInfo] = useState(false);
    const [collapsedSportCategories, setCollapsedSportCategories] = useState(() => new Set());

    useEffect(() => {
        fetchProfileSport().then((sport) => {
            if (!sport) {
                return;
            }
            setProfileSport(sport);
            if (state.sports.length === 0) {
                const catalogNames = new Set(
                    sportCatalog.map((entry) => (typeof entry?.name === "string" ? entry.name.trim() : ""))
                        .filter(Boolean)
                );
                if (catalogNames.has(sport.trim())) {
                    dispatch({ type: "set", key: "sports", value: [sport.trim()] });
                }
            }
        }).catch(() => undefined);
    }, [sportCatalog]);

    useEffect(() => {
        fetchSports().then((sportsData) => {
            if (!Array.isArray(sportsData)) {
                return;
            }
            setSportCatalog(sportsData.filter((sport) => typeof sport?.name === "string" && sport.name.trim()));
        }).catch(() => undefined);
    }, []);

    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }, [state]);

    const derivedParams = useMemo(() => {
        const size = sizeRange(state.sizePreference);
        return {
            sport: state.sports.join(","),
            division: state.divisions.join(",") || undefined,
            state: state.states.join(",") || undefined,
            localeType: state.localeTypes.join(",") || undefined,
            type: state.schoolType || undefined,
            ...size
        };
    }, [state]);

    const runSearch = async (nextPage = 0) => {
        if (!state.sports.length) {
            setError("Please select at least one sport to continue.");
            return;
        }
        setError("");
        setLoading(true);
        try {
            const response = await searchColleges({
                ...derivedParams,
                page: nextPage,
                size: 9
            });
            setResults(dedupeCollegesByIpeds(response.content || []));
            setPage(response.number || 0);
            setTotalPages(response.totalPages || 0);
            setTotalElements(response.totalElements || 0);
        } catch (e) {
            setError(collegeSearchErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    const saveCollege = async (college) => {
        if (!athleteUsername) return;
        setSavingId(college.ipedsId);
        try {
            await saveCollegeToMyList(athleteUsername, college);
            onCollegeSaved?.(college);
        } catch (e) {
            setError("Failed to save college to your list.");
        } finally {
            setSavingId("");
        }
    };

    const goNext = () => {
        const next = Math.min(state.step + 1, 5);
        dispatch({ type: "step", value: next });
        if (next === 5) runSearch(0);
    };

    const goBack = () => dispatch({ type: "step", value: Math.max(state.step - 1, 1) });

    const toggleSportCategory = (category) => {
        setCollapsedSportCategories((prev) => {
            const next = new Set(prev);
            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }
            return next;
        });
    };

    const startOver = () => {
        sessionStorage.removeItem(STORAGE_KEY);
        dispatch({ type: "reset" });
        setResults([]);
        setPage(0);
        setTotalPages(0);
        setTotalElements(0);
    };

    const sportGroups = useMemo(() => {
        const groups = groupSportsByCategory(sportCatalog);
        const catalogNames = new Set(sportCatalog.map((sport) => sport.name));
        const grandfatheredSports = Array.from(new Set(
            [...state.sports, profileSport]
                .map((sport) => (typeof sport === "string" ? sport.trim() : ""))
                .filter((sport) => sport.length > 0 && !catalogNames.has(sport))
        ));

        if (grandfatheredSports.length === 0) {
            return groups;
        }

        return [
            ...groups,
            {
                category: "Your profile sports",
                sports: grandfatheredSports.map((name) => ({ id: name, name })),
            },
        ];
    }, [sportCatalog, state.sports, profileSport]);

    return (
        <div className={`cw-container ${state.step < 5 ? "cw-static-height" : ""}`}>
            <div className="cw-header">
                <h3>Advanced search</h3>
                <p>Step {state.step} of 5</p>
                <div className="cw-progress">
                    <span style={{ width: `${(state.step / 5) * 100}%` }} />
                </div>
            </div>
            {error && <div className="cw-error" role="alert">{error}</div>}

            {state.step === 1 && (
                <div className="cw-step">
                    <div className="cw-section-title">Sports and Division</div>
                    <label>Sports</label>
                    {profileSport && (
                        <button
                            type="button"
                            className={state.sports.includes(profileSport) ? "cw-profile-sport active" : "cw-profile-sport"}
                            onClick={() => dispatch({ type: "toggle", key: "sports", value: profileSport })}
                        >
                            Profile sport: {profileSport}
                        </button>
                    )}
                    {sportGroups.map((group) => {
                        const isCollapsed = collapsedSportCategories.has(group.category);
                        return (
                            <div key={group.category} className="cw-sport-category">
                                <button
                                    type="button"
                                    className="cw-sport-category-toggle"
                                    onClick={() => toggleSportCategory(group.category)}
                                    aria-expanded={!isCollapsed}
                                >
                                    <span className="cw-sport-category-title">{group.category}</span>
                                    <span className="cw-sport-category-chevron" aria-hidden="true">
                                        {isCollapsed ? "▸" : "▾"}
                                    </span>
                                </button>
                                {!isCollapsed && (
                                    <div className="cw-pill-row">
                                        {group.sports.map((sport) => (
                                            <button
                                                key={sport.name}
                                                type="button"
                                                className={state.sports.includes(sport.name) ? "cw-pill active" : "cw-pill"}
                                                onClick={() => dispatch({ type: "toggle", key: "sports", value: sport.name })}
                                            >
                                                {sport.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <label>Divisions</label>
                    <div className="cw-pill-row">
                        {["NCAA_D1", "NCAA_D2", "NCAA_D3", "NAIA", "NJCAA"].map((division) => (
                            <button key={division} type="button" className={state.divisions.includes(division) ? "cw-pill active" : "cw-pill"} onClick={() => dispatch({ type: "toggle", key: "divisions", value: division })}>
                                {divisionFilterLabel(division)}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {state.step === 2 && (
                <div className="cw-step">
                    <div className="cw-section-title cw-section-title-with-info">
                        <span>Location</span>
                        <button
                            type="button"
                            className="cw-info-icon"
                            aria-label="Show location filter help"
                            onClick={() => setShowLocationInfo((prev) => !prev)}
                        >
                            i
                        </button>
                    </div>
                    {showLocationInfo && (
                        <div className="cw-inline-help">
                            If no states are selected, we search across all states.
                        </div>
                    )}
                    <label>States</label>
                    <div className="cw-checkbox-grid">
                        {US_STATES.map((code) => (
                            <label key={code} className="cw-state-checkbox">
                                <input
                                    type="checkbox"
                                    checked={state.states.includes(code)}
                                    onChange={() => dispatch({ type: "toggle", key: "states", value: code })}
                                />
                                <span className="cw-state-code">{code}</span>
                            </label>
                        ))}
                    </div>
                    <label>Setting</label>
                    <div className="cw-pill-row">
                        {["CITY", "SUBURB", "TOWN", "RURAL"].map((value) => (
                            <button key={value} type="button" className={state.localeTypes.includes(value) ? "cw-pill active" : "cw-pill"} onClick={() => dispatch({ type: "toggle", key: "localeTypes", value })}>{value}</button>
                        ))}
                    </div>
                </div>
            )}

            {state.step === 3 && (
                <div className="cw-step">
                    <div className="cw-section-title">School Size</div>
                    {["SMALL", "MEDIUM", "LARGE", "NO_PREFERENCE"].map((value) => (
                        <div key={value} className="cw-size-option">
                            <label className="cw-radio-row">
                                <input type="radio" checked={state.sizePreference === value} onChange={() => dispatch({ type: "set", key: "sizePreference", value })} />
                                <span className="cw-radio-label">
                                    {value === "NO_PREFERENCE" ? "No preference" : value}{" "}
                                    <span className="cw-radio-hint">({SIZE_OPTION_COPY[value]})</span>
                                </span>
                            </label>
                        </div>
                    ))}
                </div>
            )}

            {state.step === 4 && (
                <div className="cw-step">
                    <div className="cw-section-title">Public / Private</div>
                    <div className="cw-pill-row">
                        {[
                            ["", "No preference"],
                            ["PUBLIC", "Public"],
                            ["PRIVATE_NP", "Private Nonprofit"],
                            ["PRIVATE_FP", "Private For-profit"]
                        ].map(([value, label]) => (
                            <button key={value || "any"} type="button" className={state.schoolType === value ? "cw-pill active" : "cw-pill"} onClick={() => dispatch({ type: "set", key: "schoolType", value })}>{label}</button>
                        ))}
                    </div>
                </div>
            )}

            {state.step === 5 && (
                <div className="cw-step">
                    <div className="cw-actions-row">
                        <button type="button" onClick={() => dispatch({ type: "step", value: 4 })}>Refine Search</button>
                        <button type="button" onClick={startOver}>Start Over</button>
                    </div>
                    {loading && results.length === 0 ? (
                        <div className="cw-skeleton-grid">
                            {Array.from({ length: 6 }).map((_, idx) => <div key={idx} className="cw-skeleton-card" />)}
                        </div>
                    ) : results.length === 0 ? (
                        <div className="cw-empty">No colleges found — try broadening your filters</div>
                    ) : (
                        <>
                            {totalElements > 0 && (
                                <p className="cw-results-summary">{totalElements} colleges found</p>
                            )}
                            <div className={`cw-grid ${loading ? "is-loading" : ""}`}>
                            {results.map((college) => (
                                <div key={college.ipedsId} className="cw-card">
                                    <div className="cw-card-title">{college.name}</div>
                                    <div>{college.city}, {college.state}</div>
                                    <span className={divisionColor(college.division)}>{college.division}</span>
                                    <div>Size: {college.size?.toLocaleString() || "—"}</div>
                                    <div>Setting: {college.localeType || "—"}</div>
                                    <button type="button" onClick={() => saveCollege(college)} disabled={savingId === college.ipedsId}>
                                        {savingId === college.ipedsId ? "Saving..." : "Save to My List"}
                                    </button>
                                </div>
                            ))}
                            </div>
                        </>
                    )}
                    {totalPages > 1 && (
                        <div className="cw-actions-row">
                            <button type="button" disabled={page <= 0 || loading} onClick={() => runSearch(page - 1)}>Previous</button>
                            <span>Page {page + 1} of {totalPages}</span>
                            <button type="button" disabled={page >= totalPages - 1 || loading} onClick={() => runSearch(page + 1)}>Next</button>
                        </div>
                    )}
                </div>
            )}

            {state.step < 5 && (
                <div className="cw-actions-row cw-step-nav-actions">
                    <button type="button" onClick={goBack} disabled={state.step === 1}>Back</button>
                    <button type="button" onClick={goNext}>Next</button>
                </div>
            )}
        </div>
    );
}
